/**
 * Detailed SEO Extension — on-page SEO metadata inspector
 *
 * Declares one web-accessible-resource group listing seven explicit files, matched to
 * `<all_urls>` with no `use_dynamic_url` — so each is fetchable from any page at a fixed
 * URL. Its only exposed image (assets/d-logo-*.png) carries a build hash that rotates
 * each release, so we avoid it and probe the stable, vendor-namespaced content scripts
 * under src/content/ instead.
 *
 * A successful fetch of those paths is conclusive — they are served only when the
 * extension is installed.
 *
 * Manifest verified against version 2.2.7.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'pfjdepjjfjjahkjfpkcgfmfhmnakjfba';

export const detailedSeo: ExtensionDefinition = {
  id: ID,
  name: 'Detailed SEO Extension',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 600_000,
  notes:
    'Seven explicit WAR on <all_urls>, no dynamic URL. The only image is build-hashed, ' +
    'so detection fetches the stable src/content content scripts instead.',
  signals: [
    {
      id: 'war-content-primary-main',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/content/primary/main.js — the primary content script at a stable, ' +
        'non-hashed path exposed to every origin.',
      weight: 1,
      run: () => probeFetch(ID, 'src/content/primary/main.js'),
    },
    {
      id: 'war-content-google-script',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/content/googleScript/index.js — a second stable content-script path, ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'src/content/googleScript/index.js'),
    },
    {
      id: 'war-service-worker',
      kind: 'web-accessible-resource',
      description:
        'Fetch serviceWorker.js — a third stable path exposed to every origin, ' +
        'independent of the content scripts.',
      weight: 0.5,
      run: () => probeFetch(ID, 'serviceWorker.js'),
    },
  ],
};
